/**
 * Centralized WebSocket Manager
 * Handles all WebSocket connections with proper token management and cleanup
 */

class WebSocketManager {
  constructor() {
    this.connections = new Map(); // Map of connection IDs to WebSocket instances
    this.messageHandlers = new Map(); // Map of connection IDs to message handlers
    this.reconnectTimers = new Map(); // Map of connection IDs to reconnect timers
    this.isConnecting = new Set(); // Set of connection IDs currently connecting
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.currentToken = null;
    
    // Listen for token updates
    this.setupTokenListener();
  }

  setupTokenListener() {
    // Listen for storage events (token updates)
    window.addEventListener('storage', (event) => {
      if (event.key === 'token' && event.newValue !== this.currentToken) {
        console.log('🔄 Token updated, refreshing WebSocket connections...');
        this.updateAllConnectionsWithNewToken(event.newValue);
      }
    });

    // Listen for custom token refresh events
    window.addEventListener('tokenRefreshed', (event) => {
      const newToken = event.detail?.token || localStorage.getItem('token');
      if (newToken !== this.currentToken) {
        console.log('🔄 Token refreshed event received, updating WebSocket connections...');
        this.updateAllConnectionsWithNewToken(newToken);
      }
    });
  }

  updateAllConnectionsWithNewToken(newToken) {
    if (!newToken || newToken === 'null' || newToken === 'undefined') {
      console.log('❌ Invalid token received, disconnecting all WebSockets');
      this.disconnectAll();
      return;
    }

    this.currentToken = newToken;
    
    // Reconnect all active connections with the new token
    for (const [connectionId, ws] of this.connections.entries()) {
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        console.log(`🔄 Reconnecting WebSocket: ${connectionId} with new token`);
        this.reconnect(connectionId);
      }
    }
  }

  connect(connectionId, options = {}) {
    const {
      path = '/api/ws',
      onMessage = null,
      onOpen = null,
      onClose = null,
      onError = null,
      autoReconnect = true
    } = options;

    // Prevent multiple simultaneous connections for the same ID
    if (this.isConnecting.has(connectionId)) {
      console.log(`⚠️ Connection ${connectionId} already in progress`);
      return;
    }

    // Get current token
    const token = localStorage.getItem('token');
    if (!token || token === 'null' || token === 'undefined') {
      console.log(`❌ No valid token for connection: ${connectionId}`);
      return;
    }

    this.currentToken = token;
    this.isConnecting.add(connectionId);

    // Close existing connection if any
    this.disconnect(connectionId);

    // Build WebSocket URL
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    let wsUrl;
    
    if (process.env.NODE_ENV === 'development') {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';
      const apiHost = apiUrl.replace(/^https?:\/\//, '').replace(/\/api$/, '');
      wsUrl = `ws://${apiHost}${path}?token=${token}`;
    } else {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (apiUrl) {
        const apiHost = apiUrl.replace(/^https?:\/\//, '').replace(/\/api$/, '');
        wsUrl = `${protocol}//${apiHost}${path}?token=${token}`;
      } else {
        wsUrl = `${protocol}//${window.location.host}${path}?token=${token}`;
      }
    }

    console.log(`🔌 Creating WebSocket connection: ${connectionId} -> ${wsUrl}`);

    const ws = new WebSocket(wsUrl);
    this.connections.set(connectionId, ws);

    // Store message handler
    if (onMessage) {
      this.messageHandlers.set(connectionId, onMessage);
    }

    ws.onopen = () => {
      console.log(`✅ WebSocket connected: ${connectionId}`);
      this.isConnecting.delete(connectionId);
      
      // Clear any reconnect timer
      if (this.reconnectTimers.has(connectionId)) {
        clearTimeout(this.reconnectTimers.get(connectionId));
        this.reconnectTimers.delete(connectionId);
      }

      if (onOpen) onOpen();
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        // Call the specific handler for this connection
        const handler = this.messageHandlers.get(connectionId);
        if (handler) {
          handler(data);
        }
        
        if (onMessage) onMessage(data);
      } catch (error) {
        console.error(`Error parsing WebSocket message for ${connectionId}:`, error);
      }
    };

    ws.onclose = (event) => {
      console.log(`🔌 WebSocket closed: ${connectionId}`, { code: event.code, reason: event.reason });
      this.isConnecting.delete(connectionId);
      
      if (onClose) onClose(event);

      // Auto-reconnect if enabled and not a normal close
      if (autoReconnect && event.code !== 1000) {
        this.scheduleReconnect(connectionId, options);
      }
    };

    ws.onerror = (error) => {
      console.error(`❌ WebSocket error: ${connectionId}`, error);
      this.isConnecting.delete(connectionId);
      
      if (onError) onError(error);
    };

    return ws;
  }

  scheduleReconnect(connectionId, options) {
    // Don't reconnect if we don't have a valid token
    const token = localStorage.getItem('token');
    if (!token || token === 'null' || token === 'undefined') {
      console.log(`❌ No valid token, skipping reconnect for: ${connectionId}`);
      return;
    }

    if (this.reconnectTimers.has(connectionId)) {
      return; // Already scheduled
    }

    const timer = setTimeout(() => {
      this.reconnectTimers.delete(connectionId);
      if (!this.isConnecting.has(connectionId)) {
        console.log(`🔄 Attempting to reconnect: ${connectionId}`);
        this.connect(connectionId, options);
      }
    }, this.reconnectDelay);

    this.reconnectTimers.set(connectionId, timer);
  }

  reconnect(connectionId) {
    const ws = this.connections.get(connectionId);
    if (ws) {
      const handler = this.messageHandlers.get(connectionId);
      
      // Store the original options (we'll need to reconstruct them)
      const options = {
        onMessage: handler,
        autoReconnect: true
      };
      
      // Disconnect and reconnect
      this.disconnect(connectionId);
      setTimeout(() => {
        this.connect(connectionId, options);
      }, 100);
    }
  }

  disconnect(connectionId) {
    const ws = this.connections.get(connectionId);
    if (ws) {
      try {
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
          ws.close(1000, 'Normal closure');
        }
      } catch (error) {
        console.error(`Error closing WebSocket ${connectionId}:`, error);
      }
      this.connections.delete(connectionId);
    }

    // Clear message handler
    this.messageHandlers.delete(connectionId);

    // Clear reconnect timer
    if (this.reconnectTimers.has(connectionId)) {
      clearTimeout(this.reconnectTimers.get(connectionId));
      this.reconnectTimers.delete(connectionId);
    }

    this.isConnecting.delete(connectionId);
  }

  disconnectAll() {
    console.log('🔌 Disconnecting all WebSocket connections...');
    for (const connectionId of this.connections.keys()) {
      this.disconnect(connectionId);
    }
  }

  send(connectionId, data) {
    const ws = this.connections.get(connectionId);
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify(data));
        return true;
      } catch (error) {
        console.error(`Error sending message to ${connectionId}:`, error);
        return false;
      }
    }
    console.warn(`Cannot send message to ${connectionId}: connection not open`);
    return false;
  }

  isConnected(connectionId) {
    const ws = this.connections.get(connectionId);
    return ws && ws.readyState === WebSocket.OPEN;
  }

  getConnectionState(connectionId) {
    const ws = this.connections.get(connectionId);
    if (!ws) return 'DISCONNECTED';
    
    switch (ws.readyState) {
      case WebSocket.CONNECTING: return 'CONNECTING';
      case WebSocket.OPEN: return 'OPEN';
      case WebSocket.CLOSING: return 'CLOSING';
      case WebSocket.CLOSED: return 'CLOSED';
      default: return 'UNKNOWN';
    }
  }

  // Clean up everything (call on app shutdown)
  cleanup() {
    this.disconnectAll();
    window.removeEventListener('storage', this.setupTokenListener);
    window.removeEventListener('tokenRefreshed', this.setupTokenListener);
  }
}

// Create a singleton instance
const websocketManager = new WebSocketManager();

// Clean up on page unload
window.addEventListener('beforeunload', () => {
  websocketManager.cleanup();
});

export default websocketManager;







